"use client";

import React from "react";
import { ArrowRight, BriefcaseBusiness, CircleCheck, ClipboardCheck, FileSpreadsheet, Handshake, Landmark, Mail, MonitorCog, UsersRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const opportunityAreas = [{
  title: "Financial and Accounting Support",
  description: "Support bookkeeping, accounting, reconciliations, financial reporting, year-end preparation, audit readiness, and financial file organization.",
  icon: FileSpreadsheet
}, {
  title: "Program and Funding Support",
  description: "Help clients manage program information, funding requirements, reporting deadlines, supporting documentation, and accountability processes.",
  icon: ClipboardCheck
}, {
  title: "Project and Work Plan Support",
  description: "Assist with project planning, task tracking, progress follow-up, reporting, and coordination across teams, clients, and community priorities.",
  icon: BriefcaseBusiness
}, {
  title: "Management Consulting and Advisory Work",
  description: "Support practical problem-solving, process improvement, planning, reporting, and implementation work for First Nation communities and Northern organizations.",
  icon: Handshake
}, {
  title: "Microsoft 365 and Reporting Systems",
  description: "Help build or support tools using Microsoft 365, SharePoint, Excel, Power BI, workflows, macros, and related systems that improve how information is organized and shared.",
  icon: MonitorCog
}, {
  title: "Economic Development Support",
  description: "Contribute to work that helps communities and organizations plan, manage, report on, and advance economic development opportunities.",
  icon: Landmark
}];
const successProfile = ["Wants to do meaningful work connected to First Nation communities and Northern organizations.", "Is practical, respectful, and willing to learn.", "Can work independently and follow through on commitments.", "Is comfortable solving problems when the path is not perfectly clear.", "Communicates clearly with clients and team members.", "Can organize information and help others understand what needs to happen next."];
const applicationDetails = [{
  label: "Application Email",
  value: "employment@riverbendcorp.ca"
}, {
  label: "Subject Line",
  value: "Expression of Interest – RiverBend Opportunities"
}, {
  label: "Application Status",
  value: "Open on an ongoing basis"
}, {
  label: "Opportunity Type",
  value: "Future RiverBend and possible client-related opportunities"
}];
export default function DetailsServiceReparationRoulettes() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="252460" id="interest-sections-part-2" className="bg-background text-foreground py-16 md:py-24 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-14 transition-all duration-700 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Badge variant="secondary" className="mb-4 text-secondary bg-secondary/10 border-transparent">
            General Expression of Interest
          </Badge>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-primary-500 mb-6">
            Tell us where your skills could fit.
          </h2>

          <p className="text-lg text-muted-foreground font-default leading-relaxed">
            RiverBend is often looking for the right person before a specific
            job title is finalized. Your experience, attitude, problem-solving
            ability, and interest in meaningful client work may help shape a
            future opportunity with RiverBend or with one of the clients we
            support.
          </p>
        </div>

        <div ref={contentRef} className={`grid grid-cols-1 lg:grid-cols-3 gap-8 xl:gap-12 transition-all duration-700 delay-100 transform ${isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-2 space-y-10 min-w-0">
            <div>
              <h3 className="text-2xl font-default font-bold text-primary-500 mb-5 flex items-center gap-2">
                <BriefcaseBusiness className="text-secondary size-6" />
                What this opportunity may involve
              </h3>

              <p className="text-muted-foreground font-default leading-relaxed mb-6">
                RiverBend’s work brings together financial management, reporting,
                funding accountability, project planning, systems development,
                and advisory support. General expressions of interest help us
                understand who is available, what skills they bring, and where
                there may be a strong fit for future RiverBend or client-related
                opportunities.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {opportunityAreas.map((item, i) => {
                const Icon = item.icon;
                return <div key={item.title} data-index={i} className="bg-primary-50/70 rounded-md p-5">
                      <div className="flex items-start gap-3 mb-2">
                        <Icon className="size-5 text-secondary shrink-0 mt-0.5" />
                        <h4 className="font-default font-bold text-primary-500">
                          {item.title}
                        </h4>
                      </div>

                      <p className="text-muted-foreground font-default text-sm leading-relaxed pl-8">
                        {item.description}
                      </p>
                    </div>;
              })}
              </div>
            </div>

            <Separator className="bg-border" />

            <div>
              <h3 className="text-2xl font-default font-bold text-primary-500 mb-5 flex items-center gap-2">
                <UsersRound className="text-secondary size-6" />
                Success Profile
              </h3>

              <p className="text-muted-foreground font-default mb-6 leading-relaxed">
                A strong fit for RiverBend is someone who cares about doing good
                work, building trust, solving real problems, and helping First
                Nation communities, Northern organizations, and economic
                development opportunities succeed.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {successProfile.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-3">
                    <CircleCheck className="size-5 text-secondary shrink-0 mt-0.5" />
                    <span className="text-foreground font-medium font-default">
                      {item}
                    </span>
                  </div>)}
              </div>
            </div>

            <Separator className="bg-border" />

            <div className="bg-secondary/10 border border-secondary/20 rounded-md p-6 md:p-8">
              <h3 className="text-2xl font-default font-bold text-primary-500 mb-4">
                Why send an expression of interest?
              </h3>

              <p className="text-muted-foreground font-default leading-relaxed">
                We may not always have the exact role posted, but we are often
                building capacity for upcoming projects, future RiverBend roles,
                or client needs. A general expression of interest gives us a way
                to understand your background and keep you in mind when the right
                opportunity comes forward.
              </p>
            </div>
          </div>

          <aside className="lg:col-span-1 min-w-0">
            <Card className="border-border shadow-sm rounded-lg sticky top-8">
              <CardContent className="p-6 space-y-6">
                <div>
                  <p className="text-secondary font-default font-bold tracking-wider uppercase text-sm mb-3">
                    Application Details
                  </p>

                  <h3 className="text-2xl font-default font-bold text-primary-500 mb-4">
                    General Expression of Interest
                  </h3>

                  <p className="text-muted-foreground font-default leading-relaxed">
                    Send us a brief note about the kind of work you are
                    interested in, the skills you bring, and whether you are
                    interested in RiverBend roles, client opportunities, or both.
                  </p>
                </div>

                <Separator className="bg-border" />

                <div className="space-y-4">
                  {applicationDetails.map((item, i) => <div key={item.label} data-index={i}>
                      <p className="font-semibold text-primary-500 mb-1">
                        {item.label}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {item.value}
                      </p>
                    </div>)}
                </div>

                <Separator className="bg-border" />

                <div>
                  <p className="font-semibold text-primary-500 mb-2">
                    Include in your note
                  </p>

                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <CircleCheck className="size-4 text-secondary shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">
                        What kind of work interests you
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CircleCheck className="size-4 text-secondary shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">
                        What skills or experience you would bring
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CircleCheck className="size-4 text-secondary shrink-0 mt-0.5" />
                      <span className="text-sm text-muted-foreground">
                        Whether you are interested in RiverBend roles, client
                        opportunities, or both
                      </span>
                    </li>
                  </ul>
                </div>

                <Button size="lg" className="w-full bg-secondary hover:bg-secondary/90 text-white font-default" asChild>
                  <a href="mailto:employment@riverbendcorp.ca?subject=Expression%20of%20Interest%20-%20RiverBend%20Opportunities">
                    <Mail className="mr-2 size-5" />
                    Send Expression of Interest
                  </a>
                </Button>

                <Link to="/CareerOpportunitiesPage">
                  <Button variant="outline" size="lg" className="w-full border-primary-500 text-primary-500 hover:bg-primary-500 hover:text-white font-default">
                    View Current Opportunities
                    <ArrowRight className="ml-2 size-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </aside>
        </div>
      </div>
    </section>;
}